import {
  faChevronDown,
  faChevronRight,
  faFolder,
  faPen
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import EmptyList from 'components/common/EmptyList';
import Flex from 'components/common/Flex';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Button, Collapse, Form } from 'react-bootstrap';
import { PiFoldersFill } from 'react-icons/pi';

const ArticleTreeViewItem = ({
  item,
  selectedGroup,
  selectedFolder,
  openedItems,
  setOpenedItems,
  selectedItems,
  setSelectedItems,
  selection,
  onClick,
  onEdit,
  onGroupEdit
}) => {
  const [open, setOpen] = useState(openedItems.includes(item.id));
  const [children, setChildren] = useState([]);
  const [firstChildren, setFirstChildren] = useState([]);
  const [childrenOpen, setChildrenOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const checkRef = useRef();

  const handleItemClick = e => {
    e.preventDefault();
    e.stopPropagation();

    if (item.children && item.children.length > 0) {
      setOpen(!open);
      if (onClick) onClick(item);
    } else {
      if (onClick) onClick(item);
    }
  };

  const handleEdit = e => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Edit item:', item);
    if (onEdit) onEdit(item);
  };

  const handleGroupEdit = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onGroupEdit) onGroupEdit(item);
  };

  const getChildrens = item => {
    function flatInner(children) {
      let flat = [];
      children.forEach(child => {
        if (child.children && child.children.length > 0) {
          flat = [...flat, child.id, ...flatInner(child.children)];
        } else {
          flat = [...flat, child.id];
        }
      });
      return flat;
    }

    if (item.children && item.children.length > 0) {
      return flatInner(item.children);
    } else {
      return [];
    }
  };

  const isChildrenOpen = () => {
    return openedItems.some(openedItem => firstChildren.includes(openedItem));
  };

  const handleOnExiting = () => {
    setOpenedItems(openedItems.filter(openedItem => openedItem !== item.id));
  };

  const handleEntering = () => {
    setOpenedItems([...openedItems, item.id]);
  };

  const handleSingleCheckboxChange = e => {
    e.stopPropagation();
    if (e.target.checked) {
      setSelectedItems([...selectedItems, item.id]);
    } else {
      setSelectedItems(
        selectedItems.filter(selectedItem => selectedItem !== item.id)
      );
    }
  };

  const handleParentCheckboxChange = e => {
    e.stopPropagation();
    const filteredItems = selectedItems.filter(
      selectedItem => !children.includes(selectedItem)
    );
    if (e.target.checked) {
      setSelectedItems([...filteredItems, ...children]);
    } else {
      setSelectedItems(filteredItems);
    }
  };

  useEffect(() => {
    setChildren(getChildrens(item));
    if (item.children && item.children.length > 0) {
      setFirstChildren(item.children.map(child => child.id));
    }
  }, [item]);

  useEffect(() => {
    setOpen(openedItems.includes(item.id));
  }, [openedItems, item.id]);

  useEffect(() => {
    setChildrenOpen(isChildrenOpen());
  }, [children, openedItems]);

  useEffect(() => {
    if (checkRef.current) {
      const childrenSelected = selectedItems.some(selectedItem =>
        children.includes(selectedItem)
      );
      const allChildrenSelected =
        children.length > 0 &&
        children.every(child => selectedItems.includes(child));

      if (childrenSelected && !allChildrenSelected) {
        checkRef.current.indeterminate = true;
        checkRef.current.checked = false;
      } else if (allChildrenSelected && children.length > 0) {
        checkRef.current.indeterminate = false;
        checkRef.current.checked = true;
      } else {
        checkRef.current.indeterminate = false;
        checkRef.current.checked = false;
      }
    }
  }, [selectedItems, children]);

  // Identificação correta de grupos e pastas
  const isGroup = item.children && item.children.length >= 0 && !item.groupId;
  const isFolder = item.groupId !== undefined; // Pastas têm groupId

  return (
    <li className="treeview-list-item">
      {isGroup ? (
        <>
          <Flex
            justifyContent={'between'}
            alignItems={'center'}
            className="toggle-container"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Flex direction="column" className="flex-grow-1 py-1">
              <a
                className={classNames('mb-1', {
                  collapsed: !open
                })}
                href="#!"
                onClick={handleItemClick}
              >
                <p
                  className={classNames('fs-9 d-flex align-items-center m-0', {
                    'text-primary fw-semi-bold': open,
                    'text-secondary': !open
                  })}
                  style={{ transition: 'all 0.2s ease-in-out' }}
                >
                  <FontAwesomeIcon
                    icon={open ? faChevronDown : faChevronRight}
                    size="sm"
                    className="me-1"
                  />
                  <PiFoldersFill className="me-1" />
                  <span>{item.name}</span>
                </p>
              </a>
            </Flex>

            {isHovered && (
              <Button
                size="sm"
                variant="link"
                className="p-0 text-primary"
                onClick={handleGroupEdit}
                title="Editar grupo"
              >
                <FontAwesomeIcon icon={faPen} />
              </Button>
            )}
          </Flex>

          <Collapse
            in={open}
            onExiting={handleOnExiting}
            onEntering={handleEntering}
          >
            <ul
              className={classNames('treeview-list', {
                'collapse-hidden': !open,
                'collapse-show treeview-border': open,
                'treeview-border-transparent': childrenOpen
              })}
            >
              {selection &&
                open &&
                item.children &&
                item.children.length > 0 && (
                  <Flex className={'mb-1'} alignItems={'center'}>
                    <Form.Check
                      type="checkbox"
                      className="me-2"
                      onChange={handleParentCheckboxChange}
                      onClick={e => e.stopPropagation()}
                      ref={checkRef}
                    />
                    <span className="fs-10 text-muted">
                      ({item.children.length} pastas)
                    </span>
                  </Flex>
                )}
              {item.children &&
                item.children.map((nestedItem, index) => (
                  <ArticleTreeViewItem
                    key={`${nestedItem.id}-${index}`}
                    item={nestedItem}
                    index={index}
                    selectedGroup={selectedGroup}
                    selectedFolder={selectedFolder}
                    openedItems={openedItems}
                    setOpenedItems={setOpenedItems}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    selection={selection}
                    onClick={onClick}
                    onEdit={onEdit}
                    onGroupEdit={onGroupEdit}
                  />
                ))}
            </ul>
          </Collapse>
        </>
      ) : (
        // Renderização das pastas com hierarquia visual
        <Flex
          justifyContent={'between'}
          alignItems={'center'}
          className={classNames('treeview-item ps-0')}
          style={{ paddingLeft: '1rem' }} // Indentação para criar hierarquia visual
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="d-flex align-items-center flex-grow-1">
            <a
              href="#!"
              className="flex-grow-1 text-decoration-none"
              onClick={handleItemClick}
            >
              <p
                className={classNames('d-flex align-items-center fs-9 mb-0', {
                  // Fonte menor para pastas
                  'text-primary fw-semi-bold':
                    selectedItems.includes(item.id) ||
                    selectedFolder === item.id,
                  'text-secondary':
                    selectedFolder !== item.id &&
                    !selectedItems.includes(item.id)
                })}
                style={{
                  padding: '0.25rem 0'
                }}
              >
                {(isHovered && selection) || selectedItems.includes(item.id) ? (
                  <Form.Check
                    type="checkbox"
                    className="me-2"
                    onChange={handleSingleCheckboxChange}
                    checked={selectedItems.includes(item.id)}
                    onClick={e => e.stopPropagation()}
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faFolder}
                    className={classNames('me-2', {
                      'text-primary':
                        selectedItems.includes(item.id) ||
                        selectedFolder === item.id,
                      'text-secondary':
                        !selectedItems.includes(item.id) &&
                        selectedFolder !== item.id
                    })}
                  />
                )}
                {item.name}
              </p>
            </a>
          </div>

          {isHovered && (
            <Button
              size="sm"
              variant="link"
              className="p-0 text-primary ms-1"
              onClick={handleEdit}
              title="Editar pasta"
            >
              <FontAwesomeIcon icon={faPen} size="sm" />
            </Button>
          )}
        </Flex>
      )}
    </li>
  );
};

const ArticleTreeView = ({
  data,
  selection,
  selectedGroup,
  selectedFolder,
  expanded = [],
  selectedItems = [],
  setSelectedItems,
  onClick,
  onEdit,
  onGroupEdit
}) => {
  const [openedItems, setOpenedItems] = useState(expanded);

  // Sincronizar com as props expanded quando mudarem
  useEffect(() => {
    setOpenedItems(expanded);
  }, [expanded]);

  return (
    <ul className="treeview treeview-select">
      {data.length === 0 ? (
        <EmptyList imgSize={200} />
      ) : (
        data.map((treeviewItem, index) => (
          <ArticleTreeViewItem
            key={`${treeviewItem.id}-${index}`}
            item={treeviewItem}
            selectedGroup={selectedGroup}
            selectedFolder={selectedFolder}
            openedItems={openedItems}
            setOpenedItems={setOpenedItems}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            selection={selection}
            onClick={onClick}
            onEdit={onEdit}
            onGroupEdit={onGroupEdit}
          />
        ))
      )}
    </ul>
  );
};

ArticleTreeViewItem.propTypes = {
  item: PropTypes.object.isRequired,
  openedItems: PropTypes.array.isRequired,
  setOpenedItems: PropTypes.func.isRequired,
  selectedItems: PropTypes.array,
  setSelectedItems: PropTypes.func,
  selection: PropTypes.bool,
  onClick: PropTypes.func,
  onEdit: PropTypes.func,
  onGroupEdit: PropTypes.func
};

ArticleTreeView.propTypes = {
  data: PropTypes.array.isRequired,
  selection: PropTypes.bool,
  expanded: PropTypes.array,
  selectedItems: PropTypes.array,
  setSelectedItems: PropTypes.func,
  onClick: PropTypes.func,
  onEdit: PropTypes.func,
  onGroupEdit: PropTypes.func
};

export default ArticleTreeView;
